import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw } from 'lucide-react';
import BackgroundBlobs from '../components/BackgroundBlobs';
import ReferralListPanel from '../components/ReferralListPanel';
import ProfileModal from '../components/ProfileModal';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';

const ReferralsListPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading } = useAuth();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ active: 0, inactive: 0, total: 0 });
  const [referralTab, setReferralTab] = useState('active');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedUserProfile, setSelectedUserProfile] = useState(null);
  const [showProfile, setShowProfile] = useState(false);

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, loading, navigate]);

  const loadReferrals = useCallback(async () => {
    if (!user?.id) return;

    try {
      setIsLoading(true);
      setError('');
      const { data } = await api.getUserReferrals(user.id);
      setItems(Array.isArray(data?.items) ? data.items : []);
      setSummary(data?.summary || { active: 0, inactive: 0, total: 0 });
    } catch (loadError) {
      setError(loadError?.response?.data?.detail || loadError.message || 'Failed to load referrals.');
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (user?.id && isAuthenticated) {
      loadReferrals();
    }
  }, [user?.id, isAuthenticated, loadReferrals]);

  // Open the referral user's public profile modal — mirrors the Dashboard
  // profile-open flow (show the referral snapshot instantly, then refresh with
  // the live public profile). It must not open the chat/inbox.
  const handleReferralClick = async (referral) => {
    const referralUserId = referral?.userId || referral?.id;
    if (!referralUserId) return;

    setSelectedUserProfile(referral);
    setShowProfile(true);

    try {
      const { data } = await api.getUser(referralUserId);
      if (data?.user) {
        setSelectedUserProfile(data.user);
      }
    } catch (fetchError) {
      // Keep the already-open snapshot if the live profile fetch fails.
      console.warn('Unable to refresh selected public profile', fetchError?.response?.data?.detail || fetchError.message);
    }
  };

  if (loading || !isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#fff7fb] via-[#fdfbff] to-[#fff5f5]">
      <BackgroundBlobs />
      <div className="relative z-10 mx-auto flex min-h-screen w-full max-w-4xl flex-col px-4 py-6 sm:px-6 lg:px-8">
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="inline-flex items-center gap-2 rounded-full border border-white/60 bg-white/80 px-4 py-2 text-sm font-semibold text-[#000000] shadow-sm"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Dashboard
          </button>
          <button
            type="button"
            onClick={loadReferrals}
            className="inline-flex items-center gap-2 rounded-full border border-[#9333EA]/20 bg-white/80 px-4 py-2 text-sm font-semibold text-[#9333EA] shadow-sm"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>

        <div className="mb-5">
          <h1 className="text-3xl font-bold text-[#000000] font-['Outfit']">All Referrals</h1>
          <p className="mt-2 text-sm text-[#666666] font-['DM_Sans']">
            See every referral on a full screen with more space and better readability.
          </p>
        </div>

        <ReferralListPanel
          items={items}
          summary={summary}
          referralTab={referralTab}
          onChangeTab={setReferralTab}
          isLoading={isLoading}
          error={error}
          onReferralClick={handleReferralClick}
          isFullPage
        />
      </div>

      <ProfileModal
        isOpen={showProfile}
        onClose={() => {
          setShowProfile(false);
          setSelectedUserProfile(null);
        }}
        profileData={selectedUserProfile}
      />
    </div>
  );
};

export default ReferralsListPage;
